/**
 * ERDDAP Service for Ocean Data
 * Direct data fetching from NOAA ERDDAP servers
 */

import { logger } from '../utils/logger';
import { BoundingBox } from '../types/oceanographic';
import { toast } from './toastService';
import { 
  SeaSurfaceTemperature, 
  ChlorophyllData, 
  SeaSurfaceHeight, 
  CurrentData,
  MixedLayerDepth,
  GeoCoordinate
} from '../types';
import { MockOceanDataService } from './mockOceanData';
import { retryWithBackoff } from '../utils/retryWithBackoff';
import { erddapRateLimiter } from '../utils/rateLimiter';
import { erddapCache, ERDDAPCache } from '../utils/erddapCache';
import { getConfigValue } from '../config/erddapConfig';

export interface ERDDAPDataset {
  id: string;
  variable: string;
  description: string;
  unit: string;
}

export class ERDDAPService {
  // Store the last dataset timestamp
  private static lastDatasetTimestamp: string | null = null;
  
  /**
   * Execute ERDDAP request with rate limiting and caching
   */
  private static async executeWithRateLimitAndCache<T>(
    cacheKey: string,
    fetcher: () => Promise<T>,
    cacheTTL?: number
  ): Promise<T> {
    // Check cache first
    const cachedData = erddapCache.get(cacheKey);
    if (cachedData) {
      logger.info('Data retrieved from cache', { cacheKey });
      return cachedData;
    }
    
    // Log queue status
    logger.info('ERDDAP request queued', {
      queueLength: erddapRateLimiter.getQueueLength(),
      activeRequests: erddapRateLimiter.getActiveRequests()
    });
    
    // Execute with rate limiting
    const result = await erddapRateLimiter.execute(fetcher);
    
    // Cache the result
    erddapCache.set(cacheKey, result, cacheTTL);
    
    return result;
  }
  
  // Different ERDDAP servers for different datasets
  private static readonly COASTWATCH_URL = 'https://coastwatch.noaa.gov/erddap';
  private static readonly PFEG_URL = 'https://coastwatch.pfeg.noaa.gov/erddap';
  private static readonly CWCGOM_URL = 'https://cwcgom.aoml.noaa.gov/erddap';
  private static readonly BASE_URL = 'https://coastwatch.pfeg.noaa.gov/erddap'; // default
  private static readonly ERDDAP_PROXY_URL = 'https://us-central1-oceaninsightpromobile.cloudfunctions.net/erddapProxy';

  /**
   * CoastWatch allows cross-origin image loads but blocks browser JSON reads.
   * Keep native requests direct while routing web data reads through the
   * project-owned ERDDAP proxy.
   */
  private static getBrowserSafeRequestUrl(url: string): string {
    return typeof window === 'undefined'
      ? url
      : `${this.ERDDAP_PROXY_URL}?url=${encodeURIComponent(url)}`;
  }
  
  // Global bounds
  private static readonly GLOBAL_BOUNDS = {
    minLat: -90.0,
    maxLat: 90.0,
    minLon: -180.0,
    maxLon: 180.0
  };
  
  // Available datasets from ERDDAP - Using verified datasets
  private static readonly DATASETS = {
    SST: {
      id: 'noaacwBLENDEDsstDaily',
      variable: 'analysed_sst',
      description: 'NOAA Blended SST Daily',
      unit: 'celsius'
    },
    SST_LEO: {
      id: 'noaacwLEOACSPOSSTL3SnrtCDaily',
      variable: 'sea_surface_temperature',
      description: 'NOAA LEO ACSPO SST L3S NRT Daily',
      unit: 'celsius'
    },
    SST_GRADIENT: {
      id: 'noaacwLEOACSPOSSTL3SnrtCDaily',
      variable: 'sst_gradient_magnitude',
      description: 'SST Gradient Magnitude',
      unit: 'celsius/km'
    },
    CHLOROPHYLL: {
      id: 'noaacwNPPVIIRSchlaDaily',
      variable: 'chlor_a',
      description: 'NOAA S-NPP VIIRS Chlorophyll-a Daily',
      unit: 'mg/m3'
    },
    CHLOROPHYLL_GAP_FILLED: {
      id: 'noaacwNPPN20VIIRSDINEOFDaily',
      variable: 'chlor_a',
      description: 'VIIRS Chlorophyll-a GAP-Filled (DINEOF)',
      unit: 'mg/m3'
    },
    CHLOROPHYLL_S3A: {
      id: 'noaacwS3AOLCIchlaDaily',
      variable: 'chlor_a',
      description: 'Sentinel-3A OLCI Chlorophyll-a Daily',
      unit: 'mg/m3'
    },
    CHLOROPHYLL_S3B: {
      id: 'noaacwS3BOLCIchlaDaily',
      variable: 'chlor_a',
      description: 'Sentinel-3B OLCI Chlorophyll-a Daily',
      unit: 'mg/m3'
    },
    SSH: {
      id: 'noaacwBLENDEDsshDaily',
      variable: 'sla',
      description: 'Sea Level Anomaly',
      unit: 'meters'
    },
    CURRENTS: {
      id: 'noaacwBLENDEDNRTcurrentsDaily',
      variable: 'u_current',  // We'll use u component for visualization
      description: 'Sea Surface Currents (Geostrophic)',
      unit: 'm/s'
    },
    CURRENTS_MIAMI: {
      id: 'miamicurrents',
      variable: 'u_current',
      description: 'High-Resolution Ocean Currents',
      unit: 'm/s'
    },
    AFAI_1D: {
      id: 'noaa_aoml_atlantic_oceanwatch_AFAI_1D',
      variable: 'AFAI',
      description: '1-Day Alternative Floating Algae Index',
      unit: 'index'
    },
    AFAI_3D: {
      id: 'noaa_aoml_atlantic_oceanwatch_AFAI_3D',
      variable: 'AFAI',
      description: '3-Day Alternative Floating Algae Index',
      unit: 'index'
    },
    ETOPO_DEPTH: {
      id: 'etopo360',
      variable: 'altitude',
      description: 'ETOPO1 Global Relief Model',
      unit: 'meters'
    }
  };
  
  /**
   * Get the last dataset timestamp
   */
  static getLastDatasetTimestamp(): string | null {
    return this.lastDatasetTimestamp;
  }

  /**
   * Fetch Sea Surface Temperature data
   */
  static async getSST(bounds: BoundingBox, resolution: number = 0.1, targetDate?: Date, datasetVariant: 'default' | 'leo' = 'default'): Promise<SeaSurfaceTemperature[]> {
    try {
      const dataset = datasetVariant === 'leo' ? this.DATASETS.SST_LEO : this.DATASETS.SST;
      const cacheKey = ERDDAPCache.generateKey(dataset.id, bounds, resolution, targetDate);
      const url = this.buildDataURL(dataset, bounds, resolution, datasetVariant === 'leo' ? 'sstLeo' : 'sst', targetDate);
      
      logger.info('Fetching SST data from ERDDAP', { 
        url, 
        dataset: dataset.id, 
        targetDate: targetDate?.toISOString(), 
        variant: datasetVariant
      });
      
      return await this.executeWithRateLimitAndCache(
        cacheKey,
        () => retryWithBackoff(
          async () => {
            const response = await fetch(this.getBrowserSafeRequestUrl(url));
            if (!response.ok) {
              const errorText = await response.text().catch(() => 'No error details');
              logger.error(`ERDDAP SST request failed`, 
                new Error(`${response.status} ${response.statusText}`),
                { 
                  status: response.status,
                  statusText: response.statusText,
                  url,
                  dataset: dataset.id,
                  targetDate: targetDate?.toISOString(),
                  errorText 
                });
              throw new Error(`ERDDAP SST request failed: ${response.status} ${response.statusText}`);
            }
            
            const data = await response.json();
            return this.parseERDDAPData(data, dataset, 'sst');
          },
          {
            maxRetries: getConfigValue('MAX_RETRIES'),
            initialDelay: getConfigValue('INITIAL_RETRY_DELAY_MS'),
            maxDelay: getConfigValue('MAX_RETRY_DELAY_MS'),
            infiniteRetryFor503: getConfigValue('INFINITE_RETRY_FOR_503'),
            onRetry: (attempt, delay, error) => {
              const is503 = error.message?.includes('503') || (error as any).status === 503;
              const delaySeconds = Math.round(delay / 1000);
              
              logger.info(`Retrying SST request (attempt ${attempt}) after ${Math.round(delay)}ms`, {
                dataset: dataset.id,
                error: error.message,
                is503Error: is503,
                willKeepRetrying: is503 && getConfigValue('INFINITE_RETRY_FOR_503')
              });
              
              // Show user-friendly retry message for 503 errors
              if (is503 && attempt === 1) {
                toast.info(`NOAA servers busy. Retrying quickly...`);
              } else if (is503 && attempt % 10 === 0) {
                // Every 10 attempts show progress
                toast.info(`Still trying... (attempt ${attempt})`);
              }
            }
          }
        )
      );
    } catch (error) {
      logger.error('Failed to fetch SST data after all retries', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  /**
   * Fetch Chlorophyll data
   */
  static async getChlorophyll(bounds: BoundingBox, resolution: number = 0.1, targetDate?: Date, datasetVariant: 'default' | 'gapFilled' | 's3a' | 's3b' = 'default'): Promise<ChlorophyllData[]> {
    try {
      let dataset;
      switch (datasetVariant) {
        case 'gapFilled':
          dataset = this.DATASETS.CHLOROPHYLL_GAP_FILLED;
          break;
        case 's3a':
          dataset = this.DATASETS.CHLOROPHYLL_S3A;
          break;
        case 's3b':
          dataset = this.DATASETS.CHLOROPHYLL_S3B;
          break;
        default:
          dataset = this.DATASETS.CHLOROPHYLL;
      }
      
      const cacheKey = ERDDAPCache.generateKey(dataset.id, bounds, resolution, targetDate);
      const url = this.buildDataURL(dataset, bounds, resolution, 'chlorophyll', targetDate);
      
      logger.info('Fetching Chlorophyll data from ERDDAP', { 
        url, 
        dataset: dataset.id, 
        targetDate: targetDate?.toISOString(),
        variant: datasetVariant 
      });
      
      return await this.executeWithRateLimitAndCache(
        cacheKey,
        () => retryWithBackoff(
          async () => {
            const response = await fetch(this.getBrowserSafeRequestUrl(url));
            if (!response.ok) {
              const errorText = await response.text().catch(() => 'No error details');
              logger.error(`ERDDAP Chlorophyll request failed`, 
                new Error(`${response.status} ${response.statusText}`),
                { 
                  status: response.status,
                  statusText: response.statusText,
                  url,
                  dataset: dataset.id,
                  targetDate: targetDate?.toISOString(),
                  errorText 
                });
              throw new Error(`ERDDAP Chlorophyll request failed: ${response.status} ${response.statusText}`);
            }
            
            const data = await response.json();
            return this.parseERDDAPData(data, dataset, 'chlorophyll');
          },
          {
            maxRetries: getConfigValue('MAX_RETRIES'),
            initialDelay: getConfigValue('INITIAL_RETRY_DELAY_MS'),
            maxDelay: getConfigValue('MAX_RETRY_DELAY_MS'),
            infiniteRetryFor503: getConfigValue('INFINITE_RETRY_FOR_503'),
            onRetry: (attempt, delay, error) => {
              const is503 = error.message?.includes('503') || (error as any).status === 503;
              const delaySeconds = Math.round(delay / 1000);
              
              logger.info(`Retrying Chlorophyll request (attempt ${attempt}) after ${Math.round(delay)}ms`, {
                dataset: dataset.id,
                error: error.message,
                is503Error: is503,
                willKeepRetrying: is503 && getConfigValue('INFINITE_RETRY_FOR_503')
              });
              
              // Show user-friendly retry message for 503 errors
              if (is503 && attempt === 1) {
                toast.info(`NOAA servers busy. Retrying quickly...`);
              } else if (is503 && attempt % 10 === 0) {
                toast.info(`Still trying... (attempt ${attempt})`);
              }
            }
          }
        )
      );
    } catch (error) {
      logger.error('Failed to fetch Chlorophyll data', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  /**
   * Fetch Chlorophyll Gap-Filled data
   */
  static async getChlorophyllGapFilled(bounds: BoundingBox, resolution: number = 0.1, targetDate?: Date): Promise<ChlorophyllData[]> {
    return this.getChlorophyll(bounds, resolution, targetDate, 'gapFilled');
  }

  /**
   * Fetch Sea Surface Height data
   */
  static async getSSH(bounds: BoundingBox, resolution: number = 0.1, targetDate?: Date): Promise<SeaSurfaceHeight[]> {
    try {
      const dataset = this.DATASETS.SSH;
      const cacheKey = ERDDAPCache.generateKey(dataset.id, bounds, resolution, targetDate);
      const url = this.buildDataURL(dataset, bounds, resolution, 'ssh', targetDate);
      
      logger.info('Fetching SSH data from ERDDAP', { url });
      
      return await this.executeWithRateLimitAndCache(
        cacheKey,
        () => retryWithBackoff(
          async () => {
            const response = await fetch(this.getBrowserSafeRequestUrl(url));
            if (!response.ok) {
              const errorText = await response.text().catch(() => 'No error details');
              logger.warn(`ERDDAP SSH request failed: ${response.status} ${response.statusText}`, { errorText });
              throw new Error(`ERDDAP SSH request failed: ${response.status} ${response.statusText}`);
            }
            
            const data = await response.json();
            return this.parseERDDAPData(data, dataset, 'ssh');
          },
          {
            maxRetries: getConfigValue('MAX_RETRIES'),
            initialDelay: getConfigValue('INITIAL_RETRY_DELAY_MS'),
            maxDelay: getConfigValue('MAX_RETRY_DELAY_MS'),
            infiniteRetryFor503: getConfigValue('INFINITE_RETRY_FOR_503'),
            onRetry: (attempt, delay, error) => {
              logger.info(`Retrying SSH request (attempt ${attempt}) after ${Math.round(delay)}ms`, {
                error: error.message
              });
            }
          }
        )
      );
    } catch (error) {
      logger.error('Failed to fetch SSH data', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  /**
   * Fetch Ocean Current data
   */
  static async getCurrents(bounds: BoundingBox, resolution: number = 0.1, targetDate?: Date): Promise<CurrentData[]> {
    try {
      const dataset = this.DATASETS.CURRENTS;
      const cacheKey = ERDDAPCache.generateKey(dataset.id, bounds, resolution, targetDate);
      const url = this.buildDataURL(dataset, bounds, resolution, 'currents', targetDate);
      
      logger.info('Fetching Current data from ERDDAP', { url });
      
      return await this.executeWithRateLimitAndCache(
        cacheKey,
        () => retryWithBackoff(
          async () => {
            const response = await fetch(this.getBrowserSafeRequestUrl(url));
            if (!response.ok) {
              const errorText = await response.text().catch(() => 'No error details');
              logger.warn(`ERDDAP Currents request failed: ${response.status} ${response.statusText}`, { errorText });
              throw new Error(`ERDDAP Currents request failed: ${response.status} ${response.statusText}`);
            }
            
            const data = await response.json();
            return this.parseERDDAPData(data, dataset, 'currents');
          },
          {
            maxRetries: getConfigValue('MAX_RETRIES'),
            initialDelay: getConfigValue('INITIAL_RETRY_DELAY_MS'),
            maxDelay: getConfigValue('MAX_RETRY_DELAY_MS'),
            infiniteRetryFor503: getConfigValue('INFINITE_RETRY_FOR_503'),
            onRetry: (attempt, delay, error) => {
              logger.info(`Retrying Currents request (attempt ${attempt}) after ${Math.round(delay)}ms`, {
                error: error.message
              });
            }
          }
        )
      );
    } catch (error) {
      logger.error('Failed to fetch Current data', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  /**
   * Calculate min and max values from SST data
   */
  static calculateSSTRange(sstData: SeaSurfaceTemperature[]): { min: number; max: number } {
    if (!sstData || sstData.length === 0) {
      return { min: 0, max: 35 }; // Default range
    }

    const temperatures = sstData
      .map(item => item.temperature)
      .filter(temp => !isNaN(temp) && isFinite(temp));

    if (temperatures.length === 0) {
      return { min: 0, max: 35 }; // Default range
    }

    const min = Math.min(...temperatures);
    const max = Math.max(...temperatures);

    // Add buffer to avoid edge values being invisible
    const range = max - min;
    const padding = range * 0.1; // 10% padding on each side
    
    // Also ensure minimum buffer of 1 degree
    const minPadding = Math.max(padding, 1.0);
    
    // Round to 1 decimal place for cleaner values
    return {
      min: Math.round((min - minPadding) * 10) / 10,
      max: Math.round((max + minPadding) * 10) / 10
    };
  }

  /**
   * Calculate min and max values from Chlorophyll data
   */
  static calculateChlorophyllRange(chloroData: ChlorophyllData[]): { min: number; max: number } {
    if (!chloroData || chloroData.length === 0) {
      return { min: 0.01, max: 50 }; // Default range for chlorophyll
    }

    const concentrations = chloroData
      .map(item => item.concentration)
      .filter(conc => !isNaN(conc) && isFinite(conc) && conc > 0);

    if (concentrations.length === 0) {
      return { min: 0.01, max: 50 }; // Default range
    }

    const min = Math.min(...concentrations);
    const max = Math.max(...concentrations);

    // For chlorophyll, we typically use log scale
    // But for the range, just return the actual values
    return {
      min: Math.round(min * 100) / 100,
      max: Math.round(max * 100) / 100
    };
  }

  /**
   * Get SST overlay URL with dynamic temperature range
   * Fetches data first to determine the actual temperature range for better contrast
   */
  static async getDynamicSSTOverlayURL(bounds: BoundingBox, sstDataset: 'default' | 'leo' | 'gradient' = 'default', targetDate?: Date): Promise<string> {
    try {
      logger.info('Starting dynamic SST overlay generation', { 
        bounds, 
        targetDate: targetDate?.toISOString() || 'undefined',
        sstDataset 
      });
      
      // For gradient, just return the URL directly without fetching data
      if (sstDataset === 'gradient') {
        return this.getImageOverlayURL('sst', bounds, { sstDataset: 'gradient', targetDate });
      }
      
      // Fetch SST data with lower resolution for faster response
      let sstData;
      try {
        // getSST already has retry logic built in, so just call it directly
        sstData = await this.getSST(bounds, 1.0, targetDate, sstDataset); // Use coarser resolution for speed
      } catch (error) {
        // Never replace a selected historical image with the latest data. The
        // caller can still render the exact-date image without a dynamic range.
        throw error;
      }
      
      // Log raw temperature values for debugging
      const temperatures = sstData.map(item => item.temperature).filter(t => !isNaN(t) && isFinite(t));
      logger.info('SST data fetched for dynamic range', {
        dataPoints: sstData.length,
        temperatureCount: temperatures.length,
        sampleValues: temperatures.slice(0, 10),
        rawMin: temperatures.length > 0 ? Math.min(...temperatures) : 'no data',
        rawMax: temperatures.length > 0 ? Math.max(...temperatures) : 'no data',
        bounds
      });
      
      if (temperatures.length === 0) {
        logger.warn('No valid temperature data found, using default range');
        return this.getImageOverlayURL('sst', bounds, { sstDataset, targetDate });
      }
      
      // Calculate the temperature range from actual data
      const tempRange = this.calculateSSTRange(sstData);
      
      logger.info('Dynamic SST temperature range calculated', { 
        minC: tempRange.min, 
        maxC: tempRange.max,
        minF: (tempRange.min * 9/5) + 32,
        maxF: (tempRange.max * 9/5) + 32,
        dataPoints: sstData.length,
        adjustedRange: `${tempRange.min}°C to ${tempRange.max}°C (${((tempRange.min * 9/5) + 32).toFixed(1)}°F to ${((tempRange.max * 9/5) + 32).toFixed(1)}°F)`
      });
      
      // Generate an overlay for the same timestamp used to calculate the range.
      const url = this.getImageOverlayURL('sst', bounds, { temperatureRange: tempRange, sstDataset, targetDate });
      logger.info('Generated dynamic SST overlay URL', { 
        urlLength: url.length,
        hadTargetDate: !!targetDate,
        usedTargetDate: !!targetDate,
        sstDataset,
        fullUrl: url
      });
      return url;
    } catch (error) {
      logger.error('Failed to fetch SST data for dynamic range', error instanceof Error ? error : new Error(String(error)));
      // Fall back to the default range, while preserving the selected date.
      return this.getImageOverlayURL('sst', bounds, { sstDataset, targetDate });
    }
  }

  /**
   * Get Chlorophyll overlay URL with dynamic concentration range
   * Fetches data first to determine the actual concentration range for better contrast
   */
  static async getDynamicChlorophyllOverlayURL(bounds: BoundingBox): Promise<string> {
    try {
      // Fetch Chlorophyll data with lower resolution for faster response
      const chloroData = await this.getChlorophyll(bounds, 0.5);
      
      // Log raw concentration values for debugging
      const concentrations = chloroData.map(item => item.concentration).filter(c => !isNaN(c) && c > 0);
      logger.info('Chlorophyll data fetched for dynamic range', {
        dataPoints: chloroData.length,
        concentrationCount: concentrations.length,
        sampleValues: concentrations.slice(0, 10),
        rawMin: concentrations.length > 0 ? Math.min(...concentrations) : 'no data',
        rawMax: concentrations.length > 0 ? Math.max(...concentrations) : 'no data'
      });
      
      // Calculate the concentration range from actual data
      const concRange = this.calculateChlorophyllRange(chloroData);
      
      logger.info('Dynamic Chlorophyll concentration range calculated', { 
        min: concRange.min, 
        max: concRange.max,
        dataPoints: chloroData.length,
        adjustedRange: `${concRange.min} to ${concRange.max} mg/m³`
      });
      
      // Generate overlay URL with dynamic range
      return this.getImageOverlayURL('chlorophyll', bounds, { chlorophyllRange: concRange });
    } catch (error) {
      logger.warn('Failed to fetch Chlorophyll data for dynamic range, using default', { 
        error: error instanceof Error ? error.message : String(error) 
      });
      // Fall back to default range if data fetch fails
      return this.getImageOverlayURL('chlorophyll', bounds);
    }
  }

  /**
   * Get all available SST dataset URLs
   */
  static getAllSSTOverlayURLs(bounds: BoundingBox): { 
    default: string; 
    leo: string;
    descriptions: {
      default: string;
      leo: string;
    }
  } {
    return {
      default: this.getImageOverlayURL('sst', bounds),
      leo: this.getImageOverlayURL('sst', bounds, { sstDataset: 'leo' }),
      descriptions: {
        default: this.DATASETS.SST.description,
        leo: this.DATASETS.SST_LEO.description
      }
    };
  }

  /**
   * Get all available chlorophyll dataset URLs
   */
  static getAllChlorophyllOverlayURLs(bounds: BoundingBox): { 
    default: string; 
    gapFilled: string;
    s3a: string; 
    s3b: string;
    descriptions: {
      default: string;
      gapFilled: string;
      s3a: string;
      s3b: string;
    }
  } {
    return {
      default: this.getImageOverlayURL('chlorophyll', bounds),
      gapFilled: this.getImageOverlayURL('chlorophyll', bounds, { chlorophyllDataset: 'gapFilled' }),
      s3a: this.getImageOverlayURL('chlorophyll', bounds, { chlorophyllDataset: 's3a' }),
      s3b: this.getImageOverlayURL('chlorophyll', bounds, { chlorophyllDataset: 's3b' }),
      descriptions: {
        default: this.DATASETS.CHLOROPHYLL.description,
        gapFilled: this.DATASETS.CHLOROPHYLL_GAP_FILLED.description,
        s3a: this.DATASETS.CHLOROPHYLL_S3A.description,
        s3b: this.DATASETS.CHLOROPHYLL_S3B.description
      }
    };
  }

  /**
   * Get the latest available chlorophyll dataset URL
   * Tries S3A first, then S3B, then falls back to default
   */
  static async getLatestChlorophyllOverlayURL(bounds: BoundingBox): Promise<string> {
    try {
      // Try S3A first (usually has most recent data)
      const s3aUrl = this.getImageOverlayURL('chlorophyll', bounds, { chlorophyllDataset: 's3a' });
      logger.info('Trying S3A chlorophyll dataset', { url: s3aUrl.substring(0, 100) });
      
      // Test if the URL is accessible
      const s3aResponse = await fetch(s3aUrl, { method: 'HEAD' }).catch(() => null);
      if (s3aResponse && s3aResponse.ok) {
        logger.info('Using S3A chlorophyll dataset');
        return s3aUrl;
      }
      
      // Try S3B if S3A fails
      const s3bUrl = this.getImageOverlayURL('chlorophyll', bounds, { chlorophyllDataset: 's3b' });
      logger.info('Trying S3B chlorophyll dataset', { url: s3bUrl.substring(0, 100) });
      
      const s3bResponse = await fetch(s3bUrl, { method: 'HEAD' }).catch(() => null);
      if (s3bResponse && s3bResponse.ok) {
        logger.info('Using S3B chlorophyll dataset');
        return s3bUrl;
      }
      
      // Fall back to default VIIRS dataset
      logger.info('Falling back to default VIIRS chlorophyll dataset');
      return this.getImageOverlayURL('chlorophyll', bounds);
    } catch (error) {
      logger.error('Error getting latest chlorophyll URL, using default', error instanceof Error ? error : new Error(String(error)));
      return this.getImageOverlayURL('chlorophyll', bounds);
    }
  }

  /**
   * Get dataset metadata URL to fetch timestamp information
   */
  static getDatasetMetadataURL(dataType: 'sst' | 'chlorophyll' | 'ssh' | 'currents' | 'weedlines', datasetVariant: 'default' | 'leo' | 'gapFilled' | 's3a' | 's3b' = 'default'): string {
    let dataset: ERDDAPDataset;
    
    if (dataType === 'sst') {
      dataset = datasetVariant === 'leo' ? this.DATASETS.SST_LEO : this.DATASETS.SST;
    } else if (dataType === 'chlorophyll') {
      switch (datasetVariant) {
        case 'gapFilled':
          dataset = this.DATASETS.CHLOROPHYLL_GAP_FILLED;
          break;
        case 's3a':
          dataset = this.DATASETS.CHLOROPHYLL_S3A;
          break;
        case 's3b':
          dataset = this.DATASETS.CHLOROPHYLL_S3B;
          break;
        default:
          dataset = this.DATASETS.CHLOROPHYLL;
      }
    } else if (dataType === 'weedlines') {
      // For weedlines, check the variant to select 1-day or 3-day
      if (datasetVariant === '3day' as any) {
        dataset = this.DATASETS.AFAI_3D;
      } else {
        dataset = this.DATASETS.AFAI_1D;
      }
    } else {
      dataset = this.DATASETS[dataType.toUpperCase() as keyof typeof this.DATASETS];
    }
    
    const serverUrl = (dataType === 'weedlines') 
      ? this.CWCGOM_URL
      : (dataType === 'currents' || dataType === 'sst' || dataType === 'chlorophyll' || dataType === 'ssh') 
      ? this.COASTWATCH_URL 
      : this.PFEG_URL;
    
    // Get metadata about the last available time
    return `${serverUrl}/griddap/${dataset.id}.json?time[(last)]`;
  }
  
  /**
   * Fetch dataset timestamp
   */
  static async fetchDatasetTimestamp(dataType: 'sst' | 'chlorophyll' | 'ssh' | 'currents' | 'weedlines', datasetVariant: 'default' | 'leo' | 'gapFilled' | 's3a' | 's3b' = 'default'): Promise<string | null> {
    try {
      const erddapUrl = this.getDatasetMetadataURL(dataType, datasetVariant);
      const url = `${this.ERDDAP_PROXY_URL}?url=${encodeURIComponent(erddapUrl)}`;
      const cacheKey = `timestamp_${dataType}_${datasetVariant}`;
      
      logger.info('Fetching dataset timestamp through Firebase ERDDAP proxy', { dataType, datasetVariant });
      
      return await this.executeWithRateLimitAndCache(
        cacheKey,
        async () => {
          const data = await retryWithBackoff(
            async () => {
              const response = await fetch(url);
              if (!response.ok) {
                logger.warn('Failed to fetch dataset timestamp', { status: response.status });
                if (response.status === 503) {
                  throw new Error(`ERDDAP timestamp request failed: ${response.status}`);
                }
                return null;
              }
              
              return await response.json();
            },
            {
              maxRetries: getConfigValue('MAX_RETRIES'),
              initialDelay: getConfigValue('INITIAL_RETRY_DELAY_MS'),
              maxDelay: getConfigValue('MAX_RETRY_DELAY_MS'),
              infiniteRetryFor503: getConfigValue('INFINITE_RETRY_FOR_503'),
              onRetry: (attempt, delay, error) => {
                logger.info(`Retrying timestamp request (attempt ${attempt}) after ${Math.round(delay)}ms`, {
                  dataType,
                  datasetVariant,
                  error: error.message
                });
              }
            }
          );
          
          if (!data) return null;
          if (data.table && data.table.rows && data.table.rows.length > 0) {
            const timestamp = data.table.rows[0][0]; // First column should be time
            this.lastDatasetTimestamp = timestamp;
            logger.info('Dataset timestamp fetched', { timestamp, dataType, datasetVariant });
            return timestamp;
          }
          
          return null;
        },
        60000 // Cache timestamps for 1 minute
      );
    } catch (error) {
      logger.error('Error fetching dataset timestamp', error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }

  /**
   * Fetch available time steps for a dataset
   * Returns array of ISO date strings representing available data dates
   */
  static async fetchAvailableTimeSteps(
    dataType: 'sst' | 'chlorophyll' | 'ssh' | 'currents' | 'weedlines',
    datasetVariant: 'default' | 'leo' | 'gradient' | 'gapFilled' | 's3a' | 's3b' | '1day' | '3day' | 'miami' = 'default',
    limit: number = 30
  ): Promise<string[]> {
    try {
      let dataset: ERDDAPDataset;
      if (dataType === 'sst' && (datasetVariant === 'leo' || datasetVariant === 'gradient')) {
        dataset = this.DATASETS.SST_LEO;
      } else if (dataType === 'sst') {
        dataset = this.DATASETS.SST;
      } else if (dataType === 'chlorophyll') {
        dataset = datasetVariant === 'gapFilled'
          ? this.DATASETS.CHLOROPHYLL_GAP_FILLED
          : datasetVariant === 's3a'
            ? this.DATASETS.CHLOROPHYLL_S3A
            : datasetVariant === 's3b'
              ? this.DATASETS.CHLOROPHYLL_S3B
              : this.DATASETS.CHLOROPHYLL;
      } else if (dataType === 'ssh') {
        dataset = this.DATASETS.SSH;
      } else if (dataType === 'weedlines') {
        dataset = datasetVariant === '3day' ? this.DATASETS.AFAI_3D : this.DATASETS.AFAI_1D;
      } else {
        dataset = datasetVariant === 'miami' ? this.DATASETS.CURRENTS_MIAMI : this.DATASETS.CURRENTS;
      }

      const serverUrl = dataType === 'weedlines' || datasetVariant === 'miami'
        ? this.CWCGOM_URL
        : this.COASTWATCH_URL;
      const firstIndex = Math.max(Math.floor(limit) - 1, 0);
      const timeAxisUrl = `${serverUrl}/griddap/${dataset.id}.json?time%5Blast-${firstIndex}:1:last%5D`;
      const url = `${this.ERDDAP_PROXY_URL}?url=${encodeURIComponent(timeAxisUrl)}`;

      logger.info('Fetching available time steps through Firebase ERDDAP proxy', { dataType, datasetVariant, limit });
      
      const response = await fetch(url);
      if (!response.ok) {
        logger.warn('Failed to fetch time steps', { status: response.status });
        return [];
      }
      
      const data = await response.json();
      if (Array.isArray(data.table?.rows)) {
        const timeSteps = data.table.rows
          .map((row: unknown[]) => row[0])
          .filter((timeStep: unknown): timeStep is string => typeof timeStep === 'string');
        logger.info('Available time steps fetched', { count: timeSteps.length });
        return timeSteps;
      }
      
      return [];
    } catch (error) {
      logger.error('Error fetching available time steps', error instanceof Error ? error : new Error(String(error)));
      return [];
    }
  }

  /**
   * Get PNG image overlay for direct map visualization
   * This matches ERDDAP's "Make A Graph" functionality
   */
  static getImageOverlayURL(
    dataType: 'sst' | 'chlorophyll' | 'ssh' | 'currents' | 'sstLeo' | 'chlorophyllS3A' | 'chlorophyllS3B' | 'weedlines' | 'sstGradient',
    bounds: BoundingBox,
    options?: {
      temperatureRange?: { min: number; max: number };
      chlorophyllRange?: { min: number; max: number };
      chlorophyllDataset?: 'default' | 'gapFilled' | 's3a' | 's3b';
      sstDataset?: 'default' | 'leo' | 'gradient';
      weedlinesDataset?: '1day' | '3day';
      currentsDataset?: 'default' | 'miami';
      targetDate?: Date;
    }
  ): string {
    // Select the appropriate dataset based on type and options
    let dataset: ERDDAPDataset;
    if (dataType === 'sst' && options?.sstDataset) {
      switch (options.sstDataset) {
        case 'leo':
          dataset = this.DATASETS.SST_LEO;
          break;
        case 'gradient':
          dataset = this.DATASETS.SST_GRADIENT;
          break;
        default:
          dataset = this.DATASETS.SST;
      }
    } else if (dataType === 'sstLeo') {
      dataset = this.DATASETS.SST_LEO;
    } else if (dataType === 'sstGradient') {
      dataset = this.DATASETS.SST_GRADIENT;
    } else if (dataType === 'chlorophyllS3A') {
      dataset = this.DATASETS.CHLOROPHYLL_S3A;
    } else if (dataType === 'chlorophyllS3B') {
      dataset = this.DATASETS.CHLOROPHYLL_S3B;
    } else if (dataType === 'chlorophyll' && options?.chlorophyllDataset) {
      switch (options.chlorophyllDataset) {
        case 'gapFilled':
          dataset = this.DATASETS.CHLOROPHYLL_GAP_FILLED;
          break;
        case 's3a':
          dataset = this.DATASETS.CHLOROPHYLL_S3A;
          break;
        case 's3b':
          dataset = this.DATASETS.CHLOROPHYLL_S3B;
          break;
        default:
          dataset = this.DATASETS.CHLOROPHYLL;
      }
    } else if (dataType === 'weedlines') {
      // Select AFAI dataset based on option
      dataset = options?.weedlinesDataset === '3day' ? this.DATASETS.AFAI_3D : this.DATASETS.AFAI_1D;
    } else if (dataType === 'currents' && options?.currentsDataset) {
      switch (options.currentsDataset) {
        case 'miami':
          dataset = this.DATASETS.CURRENTS_MIAMI;
          break;
        default:
          dataset = this.DATASETS.CURRENTS;
      }
    } else {
      dataset = this.DATASETS[dataType.toUpperCase() as keyof typeof this.DATASETS];
    }
    
    if (!dataset) {
      throw new Error(`Dataset not found for data type: ${dataType}`);
    }
    
    // Use the provided bounds directly (no regional constraints)
    // Normalize longitude to -180 to 180 range for ERDDAP
    const normalizeLon = (lon: number): number => {
      while (lon > 180) lon -= 360;
      while (lon < -180) lon += 360;
      return lon;
    };
    
    const constrainedBounds = {
      minLat: bounds.minLat,
      maxLat: bounds.maxLat,
      minLon: normalizeLon(bounds.minLon),
      maxLon: normalizeLon(bounds.maxLon)
    };
    
    // Use appropriate server based on dataset
    let serverUrl: string;
    if (dataType === 'weedlines' || (dataType === 'currents' && options?.currentsDataset === 'miami')) {
      serverUrl = this.CWCGOM_URL;  // AFAI and Miami currents datasets are on CWC GOM server
    } else if (dataType === 'currents' || dataType === 'sst' || dataType === 'chlorophyll' || dataType === 'chlorophyllS3A' || dataType === 'chlorophyllS3B' || dataType === 'ssh' || dataType === 'sstLeo' || dataType === 'sstGradient') {
      serverUrl = this.COASTWATCH_URL;  // These datasets are on main CoastWatch server
    } else {
      serverUrl = this.PFEG_URL;  // Other datasets on PFEG server
    }
    
    // Build URL exactly like ERDDAP's Make A Graph form
    // Use transparentPng for overlay
    const baseUrl = `${serverUrl}/griddap/${dataset.id}.transparentPng`;
    
    // Build query with proper ERDDAP syntax: [(start):(stride):(stop)]
    let query: string;
    
    // Format bounds to 2 decimal places for cleaner URLs
    const minLat = constrainedBounds.minLat.toFixed(2);
    const maxLat = constrainedBounds.maxLat.toFixed(2);
    const minLon = constrainedBounds.minLon.toFixed(2);
    const maxLon = constrainedBounds.maxLon.toFixed(2);
    
    // Use specified date or last available
    const timeConstraint = options?.targetDate 
      ? `[(${options.targetDate.toISOString().slice(0, 19)}Z)]`
      : '[(last)]';
    
    if (dataType === 'ssh') {
      // SSH dataset - no altitude dimension
      // Don't use stride - native resolution
      query = `?${dataset.variable}${timeConstraint}[(${minLat}):(${maxLat})][(${minLon}):(${maxLon})]`;
    } else if (dataType === 'currents') {
      // Currents dataset - need both u and v components for vectors
      if (options?.currentsDataset === 'miami') {
        // Miami currents don't use stride in the same way, and we need to add vector drawing params
        query = `?u_current${timeConstraint}[(${minLat}):(${maxLat})][(${minLon}):(${maxLon})],v_current${timeConstraint}[(${minLat}):(${maxLat})][(${minLon}):(${maxLon})]`;
      } else {
        // Use larger stride (10) for vectors to avoid overcrowding on default dataset
        query = `?u_current${timeConstraint}[(${minLat}):10:(${maxLat})][(${minLon}):10:(${maxLon})],v_current${timeConstraint}[(${minLat}):10:(${maxLat})][(${minLon}):10:(${maxLon})]`;
      }
    } else if (dataType === 'sst' || dataType === 'sstLeo' || dataType === 'sstGradient') {
      // SST dataset (regular, LEO, and gradient)
      // Don't use stride - let ERDDAP choose optimal resolution
      query = `?${dataset.variable}${timeConstraint}[(${minLat}):(${maxLat})][(${minLon}):(${maxLon})]`;
    } else if (dataType === 'chlorophyll' || dataType === 'chlorophyllS3A' || dataType === 'chlorophyllS3B') {
      // Chlorophyll dataset - has altitude at 0.0
      query = `?${dataset.variable}${timeConstraint}[(0.0)][(${minLat}):(${maxLat})][(${minLon}):(${maxLon})]`;
    } else if (dataType === 'weedlines') {
      // AFAI dataset - no altitude dimension
      query = `?${dataset.variable}${timeConstraint}[(${minLat}):(${maxLat})][(${minLon}):(${maxLon})]`;
    } else {
      // For other datasets
      query = `?${dataset.variable}${timeConstraint}[0][(${minLat}):(${maxLat})][(${minLon}):(${maxLon})]`;
    }
    
    // Use raw ERDDAP images for most data types
    // But add special parameters for certain datasets
    let graphParams: string = "";
    
    if (dataType === 'ssh') {
      // Keep SSH simple like the others - let ERDDAP handle it
      // The raw data already shows the gradients well
      graphParams = "";
    } else if (dataType === 'currents' && options?.currentsDataset === 'miami') {
      // Add vector drawing parameters for Miami currents
      graphParams = "&.draw=vectors&.vars=longitude|latitude|u_current|v_current&.colorBar=|||||&.bgColor=0xffffff00";
    }
    
    
    // Add color scale parameters for Chlorophyll with custom range
    if ((dataType === 'chlorophyll' || dataType === 'chlorophyllS3A' || dataType === 'chlorophyllS3B') && options?.chlorophyllRange) {
      const { min, max } = options.chlorophyllRange;
      graphParams += `&.colorBar=%7C%7C%7C%7C%7C&.bgColor=0xffffff00&.color=0xffffff&.legend=&`;
      graphParams += `${dataset.variable}.colorBarMinimum=${min}&${dataset.variable}.colorBarMaximum=${max}`;
      graphParams += `&${dataset.variable}.colorBarScale=Log`;
      
      logger.info('Added Chlorophyll concentration range parameters', { 
        min, 
        max,
        graphParams 
      });
    }
    
    // Add color scale parameters for SST
    if (dataType === 'sst' || dataType === 'sstLeo') {
      // Use provided temperature range or default
      const tempRange = options?.temperatureRange || { min: 15, max: 32 };
      const { min, max } = tempRange;
      
      // Different datasets use different units
      // analysed_sst (Blended) is in Kelvin, sea_surface_temperature (LEO) is in Celsius
      let minValue, maxValue;
      if (dataset.variable === 'analysed_sst') {
        // Convert Celsius to Kelvin for ERDDAP Blended dataset
        minValue = min + 273.15;
        maxValue = max + 273.15;
      } else {
        // LEO ACSPO is already in Celsius
        minValue = min;
        maxValue = max;
      }
      
      // Format: &.draw=surface&.vars=longitude|latitude|analysed_sst&.colorBar=Rainbow|C|Linear|297|305&.bgColor=0x00000000
      // Need to encode pipe characters as %7C
      graphParams += `&.draw=surface&.vars=longitude%7Clatitude%7C${dataset.variable}`;
      graphParams += `&.colorBar=Rainbow%7CC%7CLinear%7C${minValue}%7C${maxValue}`;
      graphParams += `&.bgColor=0x00000000`;
      
      logger.info('Added SST temperature range parameters', { 
        minC: min,
        maxC: max,
        minValue,
        maxValue,
        dataset: dataset.id,
        variable: dataset.variable,
        isDefault: !options?.temperatureRange,
        graphParams 
      });
    }
    
    // Add color parameters for weedlines (AFAI) - using a custom colormap
    if (dataType === 'weedlines') {
      // AFAI values typically range from negative (no algae) to positive (algae presence)
      // We want transparent background for better overlay
      graphParams += `&.draw=surface&.vars=longitude%7Clatitude%7C${dataset.variable}`;
      graphParams += `&.colorBar=%7C%7C%7C%7C%7C`; // No color bar for cleaner overlay
      graphParams += `&.bgColor=0x00000000`; // Transparent background
    }
    
    // For SSH, currents, SST, chlorophyll, and weedlines, we need to encode the URL properly
    if (dataType === 'ssh' || dataType === 'currents' || dataType === 'sst' || dataType === 'sstLeo' || dataType === 'chlorophyll' || dataType === 'chlorophyllS3A' || dataType === 'chlorophyllS3B' || dataType === 'weedlines') {
      // Log the URL before encoding
      const urlBeforeEncoding = baseUrl + query + graphParams;
      logger.info('URL before encoding', { 
        url: urlBeforeEncoding,
        dataType
      });
      
      // Encode the query part - brackets need to be encoded for URLs
      const encodedQuery = query
        .replace(/\[/g, '%5B')
        .replace(/\]/g, '%5D');
      // Don't encode graphParams - they're already properly formatted
      const url = baseUrl + encodedQuery + graphParams;
      
      // Validate the constructed URL
      if (!url || url.length === 0) {
        throw new Error(`Failed to construct valid URL for ${dataType}`);
      }
      
      logger.info('URL after encoding', { 
        urlLength: url.length,
        urlStart: url.substring(0, 150),
        urlEnd: url.substring(url.length - 50),
        dataType,
        fullUrl: url
      });
      
      return url;
    }
    
    const url = baseUrl + query + graphParams;
    
    // Log the complete URL for debugging
    logger.info(`ERDDAP URL components:`, {
      baseUrl,
      query,
      dataset: dataset.id,
      variable: dataset.variable,
      bounds: constrainedBounds
    });
    
    logger.info(`Generated ERDDAP image URL for ${dataType}`, { 
      urlLength: url.length,
      urlStart: url.substring(0, 150),
      urlEnd: url.substring(url.length - 50),
      urlParts: {
        base: baseUrl,
        query: query.substring(0, 100),
        graphParams: graphParams.substring(0, 100)
      },
      fullUrl: url
    });
    return url;
  }


  /**
   * Build data request URL
   */
  private static buildDataURL(
    dataset: ERDDAPDataset,
    bounds: BoundingBox,
    resolution: number,
    dataType?: string,
    targetDate?: Date
  ): string {
    // Use specified date or latest available time
    // MUST match exactly what getImageOverlayURL does for consistency
    const timeConstraint = targetDate 
      ? `[(${targetDate.toISOString().slice(0, 19)}Z)]`
      : '[(last)]';
    
    // Use the provided bounds directly (no regional constraints)
    // Normalize longitude to -180 to 180 range for ERDDAP
    const normalizeLon = (lon: number): number => {
      while (lon > 180) lon -= 360;
      while (lon < -180) lon += 360;
      return lon;
    };
    
    const constrainedBounds = {
      minLat: bounds.minLat,
      maxLat: bounds.maxLat,
      minLon: normalizeLon(bounds.minLon),
      maxLon: normalizeLon(bounds.maxLon)
    };
    
    // Calculate stride based on resolution
    const latStride = Math.max(1, Math.floor(resolution * 10));
    const lonStride = Math.max(1, Math.floor(resolution * 10));
    
    // Determine which server to use based on dataset
    let serverUrl: string;
    if (dataset.id === 'noaacwBLENDEDsstDaily' || 
        dataset.id === 'noaacwN20VIIRSchlaDaily' ||
        dataset.id === 'noaacwNPPN20VIIRSDINEOFDaily' || 
        dataset.id === 'noaacwBLENDEDNRTcurrentsDaily' ||
        dataset.id === 'noaacwBLENDEDsshDaily' ||
        dataset.id === 'noaacwLEOACSPOSSTL3SnrtCDaily' ||
        dataset.id === 'noaacwS3AOLCIchlaDaily' ||
        dataset.id === 'noaacwS3BOLCIchlaDaily') {
      serverUrl = this.COASTWATCH_URL;  // Main CoastWatch server
    } else {
      serverUrl = this.PFEG_URL;  // PFEG server for other datasets
    }
    
    // Build griddap URL
    // For MUR SST dataset, we need to include time dimension
    let url: string;
    if (dataset.id === 'jplMURSST41') {
      url = `${serverUrl}/griddap/${dataset.id}.json` +
        `?${dataset.variable}${timeConstraint}` +
        `[(${constrainedBounds.minLat}):${latStride}:(${constrainedBounds.maxLat})]` +
        `[(${constrainedBounds.minLon}):${lonStride}:(${constrainedBounds.maxLon})]`;
    } else if (dataset.id === 'noaacwN20VIIRSchlaDaily' ||
               dataset.id === 'noaacwNPPN20VIIRSDINEOFDaily' || 
               dataset.id === 'noaacwS3AOLCIchlaDaily' || 
               dataset.id === 'noaacwS3BOLCIchlaDaily') {
      // Chlorophyll datasets have an altitude dimension at 0.0
      url = `${serverUrl}/griddap/${dataset.id}.json` +
        `?${dataset.variable}${timeConstraint}` +
        `[(0.0)]` +  // altitude constraint for chlorophyll
        `[(${constrainedBounds.minLat}):${latStride}:(${constrainedBounds.maxLat})]` +
        `[(${constrainedBounds.minLon}):${lonStride}:(${constrainedBounds.maxLon})]`;
    } else if (dataset.id === 'noaacwBLENDEDNRTcurrentsDaily') {
      // Currents dataset needs both u and v components
      url = `${serverUrl}/griddap/${dataset.id}.json` +
        `?u_current${timeConstraint}` +
        `[(${constrainedBounds.minLat}):${latStride}:(${constrainedBounds.maxLat})]` +
        `[(${constrainedBounds.minLon}):${lonStride}:(${constrainedBounds.maxLon})],` +
        `v_current${timeConstraint}` +
        `[(${constrainedBounds.minLat}):${latStride}:(${constrainedBounds.maxLat})]` +
        `[(${constrainedBounds.minLon}):${lonStride}:(${constrainedBounds.maxLon})]`;
    } else {
      // For other datasets that might have different dimension ordering
      url = `${serverUrl}/griddap/${dataset.id}.json` +
        `?${dataset.variable}${timeConstraint}` +
        `[(${constrainedBounds.minLat}):${latStride}:(${constrainedBounds.maxLat})]` +
        `[(${constrainedBounds.minLon}):${lonStride}:(${constrainedBounds.maxLon})]`;
    }
    
    logger.info('Built data URL', {
      dataset: dataset.id,
      timeConstraint,
      targetDate: targetDate?.toISOString(),
      url
    });
    
    return url;
  }

  /**
   * Parse ERDDAP JSON response
   */
  private static parseERDDAPData(
    data: any,
    dataset: ERDDAPDataset,
    type: 'sst' | 'chlorophyll' | 'ssh' | 'currents'
  ): any[] {
    if (!data.table || !data.table.rows) {
      logger.warn('No data returned from ERDDAP');
      return [];
    }

    const rows = data.table.rows;
    const columnNames = data.table.columnNames;
    
    // Find column indices
    const timeIdx = columnNames.indexOf('time');
    const latIdx = columnNames.indexOf('latitude');
    const lonIdx = columnNames.indexOf('longitude');
    const valueIdx = columnNames.indexOf(dataset.variable);
    
    // For currents, we need both u and v components
    const uIdx = type === 'currents' ? columnNames.indexOf('u_current') : -1;
    const vIdx = type === 'currents' ? columnNames.indexOf('v_current') : -1;
    
    const results: any[] = [];
    let dataTimestamp: string | null = null;
    
    for (const row of rows) {
      // Extract the actual timestamp from the data if available
      if (timeIdx >= 0 && row[timeIdx] && !dataTimestamp) {
        dataTimestamp = row[timeIdx];
        this.lastDatasetTimestamp = dataTimestamp;
        logger.info('Extracted dataset timestamp', { dataTimestamp });
      }
      const lat = row[latIdx];
      const lon = row[lonIdx];
      const value = row[valueIdx];
      
      if (value === null || value === undefined) continue;
      
      const location: GeoCoordinate = {
        latitude: lat,
        longitude: lon
      };
      
      switch (type) {
        case 'sst':
          // Check if value needs conversion from Kelvin to Celsius
          // NOAA Blended SST is already in Celsius, not Kelvin
          const tempInCelsius = value > 200 ? value - 273.15 : value;
          results.push({
            location,
            temperature: tempInCelsius,
            timestamp: dataTimestamp || new Date().toISOString(),
            anomaly: 0
          } as any);
          break;
          
        case 'chlorophyll':
          results.push({
            location,
            concentration: value,
            timestamp: dataTimestamp || new Date().toISOString(),
            quality: 'medium'
          } as any);
          break;
          
        case 'ssh':
          results.push({
            location,
            height: value,
            anomaly: 0,
            timestamp: dataTimestamp || new Date().toISOString()
          } as any);
          break;
          
        case 'currents':
          // For currents, we need both u and v components
          const u = row[uIdx];
          const v = row[vIdx];
          
          if (u === null || v === null || u === undefined || v === undefined) continue;
          
          // Calculate speed and direction from u,v components
          const speed = Math.sqrt(u * u + v * v);
          const direction = Math.atan2(v, u) * (180 / Math.PI); // Convert to degrees
          
          results.push({
            location,
            speed: speed * 1.94384, // Convert m/s to knots
            direction: (direction + 360) % 360, // Normalize to 0-360
            u_component: u,
            v_component: v,
            timestamp: dataTimestamp || new Date().toISOString(),
            depth: 0
          } as any);
          break;
      }
    }
    
    logger.info(`Parsed ${results.length} ${type} data points from ERDDAP`);
    return results;
  }

  /**
   * Get data value at a specific coordinate
   */
  static async getDataAtPoint(
    dataType: 'sst' | 'chlorophyll' | 'ssh' | 'currents',
    coordinate: GeoCoordinate,
    datasetVariant?: 'default' | 'leo' | 'gapFilled' | 's3a' | 's3b',
    targetDate?: Date
  ): Promise<{ value: number; unit: string; formattedValue: string } | null> {
    try {
      // Create a small bounding box around the point (approximately 1km)
      const delta = 0.01; // ~1km at equator
      const bounds: BoundingBox = {
        minLat: coordinate.latitude - delta,
        maxLat: coordinate.latitude + delta,
        minLon: coordinate.longitude - delta,
        maxLon: coordinate.longitude + delta
      };

      let data: any[] = [];
      let unit = '';

      // Fetch data based on type
      switch (dataType) {
        case 'sst':
          const sstVariant = (datasetVariant === 'leo' || datasetVariant === 'default') ? datasetVariant : 'default';
          data = await this.getSST(bounds, 0.01, targetDate, sstVariant);
          unit = '°C';
          break;
        case 'chlorophyll':
          const chloroVariant = (datasetVariant === 'default' || datasetVariant === 'gapFilled' || datasetVariant === 's3a' || datasetVariant === 's3b') ? datasetVariant : 'default';
          data = await this.getChlorophyll(bounds, 0.01, targetDate, chloroVariant);
          unit = 'mg/m³';
          break;
        case 'ssh':
          data = await this.getSSH(bounds, 0.01, targetDate);
          unit = 'm';
          break;
        case 'currents':
          data = await this.getCurrents(bounds, 0.01, targetDate);
          unit = 'kts';
          break;
      }

      if (!data || data.length === 0) {
        return null;
      }

      // Find the closest data point to the requested coordinate
      let closestPoint = data[0];
      let minDistance = Number.MAX_VALUE;

      for (const point of data) {
        const distance = Math.sqrt(
          Math.pow(point.location.latitude - coordinate.latitude, 2) +
          Math.pow(point.location.longitude - coordinate.longitude, 2)
        );
        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = point;
        }
      }

      // Extract and format the value based on data type
      let value: number;
      let formattedValue: string;

      switch (dataType) {
        case 'sst':
          value = closestPoint.temperature;
          formattedValue = `${value.toFixed(1)}°C (${((value * 9/5) + 32).toFixed(1)}°F)`;
          break;
        case 'chlorophyll':
          value = closestPoint.concentration;
          formattedValue = value < 0.01 ? '<0.01 mg/m³' : `${value.toFixed(2)} mg/m³`;
          break;
        case 'ssh':
          value = closestPoint.height;
          formattedValue = `${(value * 100).toFixed(1)} cm`;
          break;
        case 'currents':
          value = closestPoint.speed;
          const direction = closestPoint.direction;
          formattedValue = `${value.toFixed(1)} kts @ ${Math.round(direction)}°`;
          break;
        default:
          return null;
      }

      return { value, unit, formattedValue };
    } catch (error) {
      logger.error(`Failed to get ${dataType} data at point`, error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }

  /**
   * Get depth (bathymetry) at a specific coordinate
   */
  static async getDepthAtPoint(
    coordinate: GeoCoordinate
  ): Promise<{ value: number; unit: string; formattedValue: string } | null> {
    try {
      const dataset = this.DATASETS.ETOPO_DEPTH;
      const server = 'https://coastwatch.pfeg.noaa.gov/erddap';
      
      // Ensure longitude is in 0-360 range for this dataset
      let lon = coordinate.longitude;
      if (lon < 0) {
        lon = lon + 360;
      }
      
      // Build URL for single point query using ETOPO1 dataset
      const url = `${server}/griddap/${dataset.id}.json?${dataset.variable}[(${coordinate.latitude}):1:(${coordinate.latitude})][(${lon}):1:(${lon})]`;
      
      const requestUrl = this.getBrowserSafeRequestUrl(url);
      logger.info('Fetching depth data from ERDDAP', { url, coordinate });
      
      const response = await fetch(requestUrl);
      if (!response.ok) {
        logger.error(`ERDDAP depth request failed`, 
          new Error(`${response.status} ${response.statusText}`),
          { status: response.status, statusText: response.statusText, url });
        return null;
      }
      
      const data = await response.json();
      
      // Parse ERDDAP response
      if (data?.table?.rows && data.table.rows.length > 0) {
        // ETOPO gives altitude (positive for land, negative for ocean depth)
        const altitude = data.table.rows[0][2]; // Third column is the altitude value
        
        if (altitude === null || altitude === undefined) {
          return null;
        }
        
        // Convert altitude to depth (negative altitude = positive depth)
        const depth = -altitude;
        
        // Format the value
        let formattedValue: string;
        if (depth <= 0) {
          // Land or sea level
          formattedValue = altitude > 0 ? `${Math.round(altitude)}m elevation` : 'Sea level';
        } else {
          // Ocean depth - convert to feet as well for US users
          const depthInFeet = depth * 3.28084;
          formattedValue = `${Math.round(depth)}m (${Math.round(depthInFeet)}ft)`;
        }
        
        return { 
          value: depth, 
          unit: 'meters', 
          formattedValue 
        };
      }
      
      return null;
    } catch (error) {
      logger.error('Failed to get depth data at point', error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }

  /**
   * Get all ocean data for a region
   */
  static async getAllOceanData(bounds: BoundingBox, resolution: number = 0.1) {
    try {
      const [sst, chlorophyll, ssh, currents] = await Promise.all([
        this.getSST(bounds, resolution).catch((error) => {
          logger.warn('SST fetch failed, using mock data', error);
          return MockOceanDataService.generateMockSST(bounds);
        }),
        this.getChlorophyll(bounds, resolution).catch((error) => {
          logger.warn('Chlorophyll fetch failed, using mock data', error);
          return MockOceanDataService.generateMockChlorophyll(bounds);
        }),
        this.getSSH(bounds, resolution).catch((error) => {
          logger.warn('SSH fetch failed, using mock data', error);
          return MockOceanDataService.generateMockSSH(bounds);
        }),
        this.getCurrents(bounds, resolution).catch((error) => {
          logger.warn('Currents fetch failed, using mock data', error);
          return MockOceanDataService.generateMockCurrents(bounds);
        })
      ]);

      // Check if we got any real data
      const hasRealData = sst.length > 0 || chlorophyll.length > 0 || ssh.length > 0 || currents.length > 0;
      
      return {
        sst,
        chlorophyll,
        ssh,
        currents,
        mld: [] as MixedLayerDepth[], // Mixed Layer Depth not available in basic ERDDAP
        metadata: {
          source: hasRealData ? 'NOAA ERDDAP (with mock fallback)' : 'Mock Data',
          resolution,
          lastUpdated: new Date(),
          region: bounds
        }
      };
    } catch (error) {
      logger.error('Failed to fetch ocean data from ERDDAP, using all mock data', error instanceof Error ? error : new Error(String(error)));
      // Return all mock data as fallback
      return MockOceanDataService.getAllMockData(bounds);
    }
  }
}
